/**
 * /ninety/todos — My Day · To-Dos.
 *
 * VISUAL REFERENCE build for the development team: reads and writes the fake
 * in-memory store in ~/components/myday/store.ts. Search, All/Mine/private
 * scope, sort, team chips, "Show done", complete, create, edit and delete all
 * work in-session and reset on reload.
 *
 * Visibility rule: private to-dos only show to their assignee (ME_ID in the
 * demo), so other people's private items never appear here.
 */
import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { cn } from '~/lib/cn';
import {
  useMyDay, todosApi, personById, fmtDate, isOverdue, ME_ID, TEAMS, type Todo,
} from '~/components/myday/store';
import {
  Panel, PanelToolbar, Avatar, SearchBox, ToolbarSelect, ClearButton, TeamChips, Switch,
  NewButton, ToolbarActions, PagerFooter, RoundCheck, EmptyState, FakeDataNote,
  IconLock, IconClip, IconTodo, MD_NAVY,
} from '~/components/myday/ui';
import { TodoDialog } from '~/components/myday/todos/TodoDialog';

export const Route = createFileRoute('/_app/ninety/todos')({
  component: TodosTab,
});

type Scope = 'all' | 'mine' | 'private';
type SortKey = 'due' | 'created' | 'title' | 'assignee';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'due', label: 'Sort: Due date' },
  { value: 'created', label: 'Sort: Created' },
  { value: 'title', label: 'Sort: Title' },
  { value: 'assignee', label: 'Sort: Assignee' },
];

/** created · to-do · due · assignee */
const GRID = 'grid grid-cols-[28px_96px_minmax(0,1fr)_100px_76px] items-center gap-3 px-4';

function TodosTab() {
  const todos = useMyDay((s) => s.todos);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [sort, setSort] = useState<SortKey>('due');
  const [team, setTeam] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  // undefined = closed, null = new, Todo = editing
  const [editing, setEditing] = useState<Todo | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = todos.filter((t) => {
      if (t.isPrivate && t.assigneeId !== ME_ID) return false;
      if (scope === 'mine' && t.assigneeId !== ME_ID) return false;
      if (scope === 'private' && !(t.isPrivate && t.assigneeId === ME_ID)) return false;
      if (team && t.team !== team) return false;
      if (!showDone && t.done) return false;
      if (q && !`${t.title} ${t.detail}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const name = (t: Todo) => personById(t.assigneeId)?.name ?? '￿';
    const time = (iso: string | null) => (iso ? new Date(iso).getTime() : Number.POSITIVE_INFINITY);
    return [...rows].sort((a, b) => {
      switch (sort) {
        case 'created': return time(b.createdAt) - time(a.createdAt);
        case 'title': return a.title.localeCompare(b.title);
        case 'assignee': return name(a).localeCompare(name(b)) || time(a.dueAt) - time(b.dueAt);
        default: return time(a.dueAt) - time(b.dueAt);
      }
    });
  }, [todos, search, scope, sort, team, showDone]);

  const visible = filtered.slice(0, pageSize);

  const clear = () => {
    setSearch(''); setScope('all'); setSort('due'); setTeam(null); setShowDone(false);
  };

  const seg = (active: boolean) => cn(
    'h-8 px-3 inline-flex items-center justify-center text-sm font-semibold transition-colors',
    active ? 'text-white' : 'bg-white text-slate-700 hover:bg-slate-50',
  );
  const segStyle = (active: boolean) => (active ? { background: MD_NAVY } : undefined);

  return (
    <div className="px-4 sm:px-6 py-4">
      <Panel>
        <PanelToolbar>
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={search} onChange={setSearch} placeholder="Search to-dos..." />
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden divide-x divide-slate-300">
              <button type="button" className={seg(scope === 'all')} style={segStyle(scope === 'all')}
                onClick={() => setScope('all')}>All</button>
              <button type="button" className={seg(scope === 'mine')} style={segStyle(scope === 'mine')}
                onClick={() => setScope('mine')}>Mine</button>
              <button type="button" aria-label="Private only" title="My private to-dos only"
                className={seg(scope === 'private')} style={segStyle(scope === 'private')}
                onClick={() => setScope(scope === 'private' ? 'all' : 'private')}>
                <IconLock className="w-3.5 h-3.5" />
              </button>
            </div>
            <ToolbarSelect value={sort} onChange={setSort} options={SORT_OPTIONS} />
            <ClearButton onClick={clear} />
          </div>
          <TeamChips value={team} onChange={setTeam} count={filtered.length} />
          <ToolbarActions>
            <Switch checked={showDone} onChange={setShowDone} label="Show done" />
            <NewButton onClick={() => setEditing(null)} />
          </ToolbarActions>
        </PanelToolbar>

        <div className="overflow-x-auto">
          <div className="min-w-[620px]">
            <div className={cn(GRID, 'py-2 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500')}>
              <span />
              <span>Created</span>
              <span className="inline-flex items-center gap-1">To-Do <IconClip className="w-3 h-3" /></span>
              <span className="text-right">Due date</span>
              <span className="text-right">Assignee</span>
            </div>

            {visible.length === 0 ? (
              <EmptyState icon={<IconTodo className="w-8 h-8" />} title="No to-dos"
                body="Nothing matches these filters. Clear them or add a new to-do." />
            ) : (
              visible.map((t) => {
                const overdue = !t.done && isOverdue(t.dueAt);
                return (
                  <div key={t.id} role="button" tabIndex={0}
                    onClick={() => setEditing(t)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditing(t); }}
                    className={cn(GRID, 'py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50')}>
                    <span onClick={(e) => e.stopPropagation()}>
                      <RoundCheck checked={t.done} label={t.done ? 'Mark not done' : 'Mark done'}
                        onChange={() => todosApi.update(t.id, { done: !t.done })} />
                    </span>
                    <span className="text-sm text-slate-500">{fmtDate(t.createdAt)}</span>
                    <span className="min-w-0 flex items-center gap-2">
                      <span className={cn('text-[15px] truncate', t.done ? 'line-through text-slate-400' : 'text-slate-900')}>
                        {t.title}
                      </span>
                      {t.attachments > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-slate-500 shrink-0">
                          <IconClip className="w-3.5 h-3.5" />{t.attachments}
                        </span>
                      )}
                      {t.isPrivate && <IconLock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    </span>
                    <span className={cn('text-sm text-right', overdue ? 'text-red-600 font-medium' : 'text-slate-500')}>
                      {fmtDate(t.dueAt)}
                    </span>
                    <span className="flex justify-end">
                      <Avatar person={personById(t.assigneeId)} />
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <PagerFooter pageSize={pageSize} onPageSize={setPageSize} shown={visible.length} total={filtered.length} />
      </Panel>
      <FakeDataNote />

      {editing !== undefined && (
        <TodoDialog todo={editing} onClose={() => setEditing(undefined)}
          defaults={{ team: team ?? TEAMS[0], assigneeId: ME_ID }} />
      )}
    </div>
  );
}
